/** `A-52c`·`A-52d`·`A-52e`. **판을 끝내는 자리의 마지막 물음**. 규격 정본은 `docs/PRD.md`
 * `7.6.14b`(**ⓐ~ⓔ**)와 목업 `A-52c`·`A-52d` 프레임, 웹 짝은 `7.7.5a`(`W-40c`·`W-40d`·
 * `W-40e`)다.
 *
 * ## ⭐ 이 자리가 비어 있었다
 *
 * `A-24e`(`7.6.7d`)는 **남은 장이 있을 때만** 뜬다. 그런데 작업을 결과 확인으로 넘기는
 * 회차는 **정의상 남은 장을 0으로 만든 회차**라, **회차 1..n-1 은 묻고 n 번째는 묻지
 * 않는다.** ⛔ **드문 갈래가 아니라 모든 작업이 그 회차를 지난다.** 그 뒤 축
 * (`A-31b` → `A-25b`)에는 `만들지 않기` 가 없어서, **셀러가 그 장을 포기하겠다고 말할
 * 자리가 제품 어디에도 없이 판이 끝났다.**
 *
 * **그러면 돈이 도는 계기가 하나도 안 남는다.** 30일 자동 반환은 **그 30일을 셀 주체가
 * 없고**(실측: `apps/api/src/worker.ts` 에 `scheduled` 0건 · `apps/api/wrangler.toml` 에
 * cron 0건), 셀러 선언은 **받을 자리가 없었다**(`13.1` O-70 의 두 경로가 동시에 비었다).
 *
 * ## ⭐ 왜 화면 파일에서 떼어 냈는가
 *
 * 앱 `apps/mobile/src/lib/` 의 `making.ts` · `result.ts` · `round-end.ts` 머리말과 같은 이유다. **JSX 안의
 * 삼항 사슬로 두면 판정을 통째로 죽여도 저장소가 초록이다** — 글자는 파일 안에 그대로 있기
 * 때문이다. 2026-09-23 에 `result.tsx` 의 승인 차단이 정확히 그 모양으로 살아 있었다.
 *
 * 이 자리에는 **죽으면 셀러 돈이 셀러 모르게 결판나는 판정이 둘** 있다.
 *   ① **`공유` 도 `받기` 도 `A-52d` 를 거친다** — 한쪽이 건너뛰면 **그 길이 몰래
 *      `만들지 않기` 가 된다**(`exportActionOpensConfirm`)
 *   ② **고르지 않으면 판이 안 나간다** — 그냥 지나가게 두면 **받기 버튼이 몰래
 *      `만들지 않기` 가 된다**(`exportConfirmOutcome`)
 *
 * ## ⭐ 왜 `packages/shared` 에 있는가 (2026-10-02. PRD `13.1` D-93 · `7.7.5a`)
 *
 * 이 판정은 처음에 `apps/mobile/src/lib/export-decline.ts` 에 섰다. 웹 `W-40c`·`W-40d`·
 * `W-40e` 가 같은 갈래를 그려야 하는데, 판정이 앱 안에 있으면 웹은 **판정과 문장을 두 벌로**
 * 들게 된다. 그것이 `7.7.3` 이 막는 일이라 **복사가 아니라 이동**으로 올렸다.
 * 앱 쪽 자리는 이 파일을 다시 내보내고, 화면이 서 있는 판매처를 거는 일만 한다.
 *
 * 함께 올라온 것이 둘 있다. 금액 라벨(`roundEndAmountLabel`, 원래 `A-24e` 의 앱 파일)과
 * 파일 목록(`exportFileList`, 원래 앱 `lib/template.ts`. 지금은 `channel.ts`)이다.
 * ⛔ **그 둘을 웹에서 다시 만들지 않는다.** 만들면 금액 라벨과 파일 수가 기기마다 갈린다.
 *
 * ## ⛔ 순수 TypeScript 만 둔다
 *
 * `packages/shared/test/export-decline.test.mjs` 가 Node 내장 타입 스트리핑으로 이 `.ts` 를
 * 그대로 불러 시험한다. ⛔ **react·react-native·DOM 을 끌어오지 마라.** 끌어오는 순간 이
 * 파일을 보는 시험이 통째로 사라지고, 위 판정 둘이 다시 아무도 안 보는 자리로 간다.
 */

import { DEFAULT_SALES_CHANNEL, exportFileList, type SalesChannel } from "./channel";
import type { DetailJobView, RecallDecision } from "./domain";
import { formatKrw } from "./format";
import {
  doneImages,
  failedImages,
  hasDeclinedRecall,
  imagesAwaitingRecallDecision,
  isJobDiscarded,
  isJobRunning,
  pendingImages,
  processingImages,
} from "./job-rules";

/* ── 금액 라벨. `A-24e` 와 `A-52c`·`A-52d` 가 함께 쓴다 ───────────────────── */

/** 금액 줄 라벨. **숫자가 아니라 그 돈이 어디로 가는가를 말한다**(`7.5.9` 판정 10).
 *
 * ⭐ **이 함수는 `A-24e`(`7.6.7d`) 의 것이다.** 이름이 `roundEnd` 인 것은 그래서다.
 * `A-52c`·`A-52d` 가 같은 물음의 마지막 회라 같은 라벨을 쓰고, 그 판정과 함께 이리로
 * 올라왔다. ⭐ **금액 라벨을 새로 만들지 않는다**(`7.6.14b` ⓑ). 글자를 두 파일에 따로 적으면
 * 한쪽만 고쳐지는 날 갈린다(`7.5.9` 규칙 2).
 *
 * ⭐ **같은 `₩500` 을 세 갈래가 다르게 부르는 것은 규칙 2 위반이 아니다.** 셋은 **다른
 * 사실**이고, **셀러가 고르는 순간 그 행선지가 실제로 갈린다.**
 *
 * ⛔ **라벨을 하나로 고정하면 그 자리가 반드시 거짓이 된다.** `다시 만들지 않으면 돌아갈
 * 금액` 은 **조건절**이라 `다시 만들기` 를 고른 뒤에는 **없는 선택지를 가리킨다.**
 * `7.5.9` 판정 7 이 `A-31b` 에서 같은 조건절을 같은 이유로 걷었다.
 *
 * ⛔ **`더 내실 금액` 을 이 줄에 쓰지 않는다.** 그 라벨은 **시트 발밑 금액 줄의 것**이고,
 * 한 시트에서 두 자리가 같은 라벨을 들면 셀러는 **둘을 같은 돈으로 읽는다.**
 * ⛔ **`돌아가는 중`·`돌아간 금액` 도 쓰지 않는다.** 결제사에 요청한 적이 **0건**이다. */
export function roundEndAmountLabel(choice: RecallDecision | null): string {
  if (choice === "RECALL") return "이 장에 쓰일 금액";
  if (choice === "DECLINE") return "돌아갈 금액";
  return "다시 만들지 않으면 돌아갈 금액";
}

/** 이 화면이 다루는 못 만든 장 하나. **`A-24e` 의 `RoundEndPage` 와 모양이 같다** — 같은
 *  물음의 마지막 회라서다. ⛔ **`choice` 가 없다.** 이 시트에는 **세그먼트가 없고**
 *  고르는 길이 **버튼 하나**다(`7.6.14b` ⓒ). */
export interface ExportDeclinePage {
  imageId: string;
  /** 셀러가 부르는 이름(`${sortOrder + 1}번 사진`). ⛔ 목록 위치로 부르지 않는다 —
   *  화면마다 다른 번호가 된다. */
  name: string;
  /** **이 장 때문에 셀러가 부담하지 않는 금액**. ⛔ 모르면 `null` 이고 **`0` 이 아니다.** */
  refundableAmount: number | null;
}

/** 같은 화면의 세 상태(`7.6.14b`).
 *
 * - `ASK`: 아직 안 나갔고 **안 고른 장이 있다.** **묻는다.**
 * - `SETTLED`: **못 만든 장 전부를 `A-24e` 에서 이미 `만들지 않기` 로 답했고 판은 아직 안
 *   나갔다**(`A-52e` · 웹 `W-40e`. `7.6.14b` ⓔ · PRD `7.13` D-92). ⭐ **시제가 두 축으로
 *   갈린다**. 장은 이미 결판났고(과거) 판은 아직 안 나갔다(미래). **묻지 않는다.**
 * - `DONE` — **내보낸 뒤.** ⭐ **새 프레임도 새 낱말도 아니고 시제와 조건절만 움직인다.**
 *   ⛔ **여기서 다시 물으면 이미 결판난 장을 또 묻는 일이다.**
 *
 * ⛔ **`SETTLED` 를 `DONE` 으로 뭉치지 마라.** 그 상태의 문장이 `나갔습니다`·`끝났고` 라서
 * **아직 안 일어난 일을 끝난 일로 말한다.** ⛔ **`ASK` 로 뭉치지도 마라**. 셀러가 이미 답한
 * 장을 판 끝에서 다시 묻게 된다. */
export type ExportDeclineStage = "ASK" | "SETTLED" | "DONE";

/** ⭐ **이 화면이 `A-52c`(또는 `A-52e`)로 서는가.** `null` 이면 `A-52`(전량 성공한 판)다. */
export interface ExportDeclineView {
  stage: ExportDeclineStage;
  /** **블록과 시트가 세우는 장들.** `ASK` 는 **묻는 장**(`imagesAwaitingRecallDecision`),
   *  `SETTLED`·`DONE` 은 못 만든 장 전부(`failedImages`). 배너 `l1` · 블록 머리 · 시트의
   *  `${n}` 이 이 배열의 길이다(`7.6.14b` ⓐ 「줄마다 세는 것이 다르다」). **비어 있으면 이 값이
   *  `null`.**
   *
   *  ⛔ **파일 목록 설명의 수는 이 길이가 아니다.** 그 줄은 **판에 안 들어가는 장**
   *  (`total − made`)을 센다. `ASK` 에서 앞 회차에 일부만 답한 판이면 둘이 갈린다
   *  (`exportDeclineFileNote`). */
  pages: ExportDeclinePage[];
  /** 올린 장수. 배너의 `8장 중` 이 이 수다. */
  total: number;
  /** 만든 장수 = **나간 장수**. 배너와 파일 목록 설명이 이 수다. */
  made: number;
  /** 받는 파일 개수. **내비 우측과 주 버튼이 같은 값을 쓴다.** */
  files: number;
}

/** ⭐⭐ **작업 하나에서 「이 화면이 무엇을 말할지」를 만든다. `null` 이면 `A-52c` 가 안 선다.**
 *
 * ⛔ **화면이 이 계산을 다시 하지 않는다.** 화면 안의 조건 한 줄은 **어떤 시험도 보지
 * 못한다.** 그리고 이 자리에는 세어야 할 것이 넷(**올린 장 · 나간 장 · 파일 · 못 만든 장**)
 * 있어서, 화면에 두면 **넷 중 하나가 조용히 틀린다.**
 *
 * ## ⭐ 상태마다 세는 배열이 다르다. 그것이 이 함수의 요점이다
 *
 * - **`ASK`** — `imagesAwaitingRecallDecision`. ⛔ **`failedImages` 를 쓰지 마라** —
 *   `A-24e` 에서 이미 결판난 장까지 세어 **셀러가 답한 물음이 판 끝에서 다시 선다.**
 *   `7.6.14b` 가 **「이미 고른 장은 다시 묻지 않는다」**고 못 박았다.
 * - **`SETTLED`**: `failedImages`. ⭐ **단, 그 전부가 이미 `만들지 않기` 로 답한 장일 때만
 *   선다**(`hasDeclinedRecall`). ⛔ **「`imagesAwaitingRecallDecision` 이 0」을 조건으로 쓰지
 *   마라**. 그 함수는 **도는 중인 작업에서도 0** 을 돌려준다(`needsRecallDecision` 이 그때
 *   묻지 않으므로). 그러면 **셀러가 아무것도 안 고른 장을 `이미 만들지 않기로 하셨습니다`
 *   라고 말한다**. 셀러가 한 적 없는 선택을 셀러의 것으로 적는 화면이다.
 * - **`DONE`** — `failedImages`. **내보내기가 서 있는 장 전부를 한 순간에 결판냈으므로**
 *   (`7.6.14b` ⓒ) 그 뒤에는 **묻는 장과 못 만든 장이 같은 집합**이다. 여기서
 *   `imagesAwaitingRecallDecision` 을 쓰면 **언제나 빈 배열**이라 **왜 파일이 하나 적은지를
 *   아무도 설명하지 않는 화면**으로 되돌아간다.
 *
 * ⚠️ **`DONE` 을 가르는 것은 작업 상태 하나다.** 시제(`나갔습니다` · `끝났고`)가 그 상태에만
 * 참이라, 다른 것으로 가르면 **화면이 아직 안 일어난 일을 끝난 일로 말한다.**
 *
 * `channel` 은 파일 수를 셀 판매처다. 화면이 서 있는 판매처를 넘긴다(앱은
 * `CURRENT_CHANNEL`). 안 넘기면 기본 판매처로 센다. */
export function exportDeclineView(
  job: DetailJobView,
  channel: SalesChannel = DEFAULT_SALES_CHANNEL,
): ExportDeclineView | null {
  const stage = exportDeclineStage(job);
  if (stage === null) return null;
  const images = stage === "ASK" ? imagesAwaitingRecallDecision(job) : failedImages(job);
  const made = doneImages(job).length;
  return {
    stage,
    pages: images.map((image) => ({
      imageId: image.id,
      name: `${image.sortOrder + 1}번 사진`,
      // ⛔ 모르면 `null` 이다. **`0` 도 단가도 지어내지 않는다**(`7.5.9` 판정 2).
      refundableAmount: image.refundableAmount ?? null,
    })),
    total: job.images.length,
    made,
    // ⛔ **파일 수를 여기서 세지 않는다.** 규격이 정하는 값이라 `docs/TEMPLATE.md` 를 읽는
    //    자리(`./channel` 의 `exportFileList`)에서 받아 온다. `A-52` 의 내비 우측도 같은 함수다.
    files: exportFileList(made, channel).length,
  };
}

/** 상태 하나를 고른다. **못 만든 장이 없으면 `null`.**
 *
 * 차례가 판정이다. **내보냈는가 → 판이 아직 움직이는가 → 아직 안 고른 장이 있는가 → 못
 * 만든 장 전부가 이미 답했는가.** ⛔ 마지막 항을 「안 고른 장이 0」으로 바꾸지 마라(위
 * `exportDeclineView` 머리말). 도는 중인 작업에서 그 값이 0 이 되어 **답하지 않은 장이 답한
 * 장으로 읽힌다.**
 *
 * ## ⭐ 판이 아직 움직이면 `ASK` 도 `SETTLED` 도 서지 않는다 (2026-10-03 코드 리뷰)
 *
 * 이 화면은 **판이 끝난 자리**의 것이다(`7.6.14b`: 작업을 결과 확인으로 넘기는 회차는 정의상
 * **남은 장을 0 으로 만든 회차**다). 남은 장이 있는 판에서 서면 세는 수가 전부 낡는다. 재현:
 * #4 는 `만들지 않기`, #6 은 되살리기로 다시 도는 중이면 못 만든 장은 #4 하나라 `SETTLED` 가 서고
 * `8장 중 6장이 나갑니다` · `파일이 1개 적은`(실제 2개)을 말한다. 서버는 그때 내보내기를
 * `ALREADY_PROCESSING` 으로 막는다.
 *
 * 세 조건을 다 본다.
 *   ① `isJobRunning`(작업 `PROCESSING`)
 *   ② `isJobDiscarded`(원본 파기). 다시 만들 밑천도 내보낼 판도 없다
 *   ③ **장 하나라도 `PENDING`·`PROCESSING`.** ⛔ **①만으로는 모자란다.** `computeJobStatus` 는
 *      나가 있는 장이 있으면 작업을 `PAID` 로 둔다. 되살리기가 줄에 서 있는 동안이 그 창이다.
 *
 * ⚠️ **`ASK` 도 같은 조건을 받는다. 그 전에는 ①②만 받았다**(`needsRecallDecision` 을 거쳐).
 * ③의 창에서 `ASK` 가 서면 `A-52d` 가 `만들지 않기` 를 **먼저 서버에 적고** 내보내기를 부르는데,
 * 내보내기가 막혀도 **그 선언은 이미 적혀 있다.** 시트가 `지금 받으면 이 작업이 끝납니다` 라고
 * 약속한 일이 일어나지 않은 채 장만 결판난다. */
function exportDeclineStage(job: DetailJobView): ExportDeclineStage | null {
  const failed = failedImages(job);
  if (failed.length === 0) return null;
  if (job.status === "EXPORTED") return "DONE";
  if (isBoardInMotion(job)) return null;
  if (imagesAwaitingRecallDecision(job).length > 0) return "ASK";
  if (failed.every(hasDeclinedRecall)) return "SETTLED";
  return null;
}

/** 판이 아직 움직이는가. 위 `exportDeclineStage` 머리말의 ①②③. */
function isBoardInMotion(job: DetailJobView): boolean {
  if (isJobRunning(job) || isJobDiscarded(job)) return true;
  return pendingImages(job).length + processingImages(job).length > 0;
}

/* ── 제한 배너 · 파일 목록 설명 ──────────────────────────────────────────── */

/** `!` 제한 배너 두 줄(`7.6.14b` ⓐ).
 *
 * ⭐ **`l2` 가 수를 다시 세지 않는다.** 바로 위 `l1` 이 **`못 만든 ${n}장`** 을 이미 셌고,
 * **한 배너가 같은 수를 두 번 세면 한쪽이 낡는다.**
 * ⛔ **`그 장은` 으로 되돌리지 마라.** 윗줄이 **`못 만든 3장은`** 이라고 세어 둔 뒤의
 * `그 장은` 은 **셋 중 하나를 가리키는 말로 읽힌다.** ⭐ **지시어가 가리킬 것이 여럿일 때는
 * 지시어를 버리고 이름을 다시 적는다.** ⛔ **`그 장들은` 도 아니다** — `들` 은 셀러가 보는
 * 문구 어디에도 없는 꼴이라, 넣으면 `n = 1` 과 `n ≥ 2` 가 두 문장으로 갈린다.
 * ⛔ **30일을 적지 않는다.** 그 문장은 `A-31b` 한 자리의 것이고 **기한은 장마다 따로
 * 흐른다**(`7.5.9` 판정 5). */
export function exportDeclineBanner(view: ExportDeclineView): { l1: string; l2: string } {
  const n = view.pages.length;
  if (view.stage === "DONE") {
    return {
      l1: `못 만든 ${n}장은 이 판에 들어가지 않았습니다.`,
      l2:
        `${view.total}장 중 ${view.made}장이 나갔습니다.` +
        " 이 작업이 끝났고, 못 만든 장은 만들지 않은 것으로 확정됐습니다.",
    };
  }
  if (view.stage === "SETTLED") {
    // `7.6.14b` ⓔ. **`l1` 은 `ASK` 와 같다**. 판이 아직 안 나갔다. ⛔ **`l2` 를 `그때
    // 결판납니다` 로 적지 않는다. 이미 결판났다.**
    return {
      l1: `못 만든 ${n}장은 이 판에 들어가지 않습니다.`,
      l2: `${view.total}장 중 ${view.made}장이 나갑니다. 못 만든 장은 이미 만들지 않기로 하셨습니다.`,
    };
  }
  return {
    l1: `못 만든 ${n}장은 이 판에 들어가지 않습니다.`,
    l2:
      `${view.total}장 중 ${view.made}장이 나갑니다.` +
      " 파일을 받으면 이 작업이 끝나고, 못 만든 장은 그때 결판납니다.",
  };
}

/** 파일 목록 아래 설명. ⭐ **왜 파일 수가 줄었는지를 말하는 유일한 줄이다.**
 *
 * 안 적으면 셀러는 **파일이 빠진 줄로 읽고** 받은 것을 세어 본다(`A-31b` 가 **왜 8장이
 * 아니라 7장인지**를 보인 것과 같은 판단).
 *
 * ⛔ **수를 걷어 `파일이 그만큼 적습니다` 로 바꾸지 마라.** 이 줄이 서는 이유가 **셀러가
 * 받은 파일을 세어 맞춰 보는 것**이라 **대조가 불가능해진다.**
 * ⛔ **`하나` 로 되돌리지 마라.** 이 화면은 파일을 **`개` 로만** 세고(`8개` ·
 * `8개 파일 받기` · `받는 파일 8개`) **`하나` 가 혼자 다른 꼴이었다.**
 *
 * ⚠️ **이 줄은 「장 하나에 파일 하나」에 매여 있다.** 오늘 파일 수는 **`나간 장수 + 1`**
 * (대표 1장)이라 **판에 안 들어간 장 수(`총장수 − 나간 장수`)만큼 정확히 줄어든다.** ⛔ **묻는
 * 장 수만큼이 아니다**(`7.6.14b` ⓐ 「줄마다 세는 것이 다르다」). `docs/TEMPLATE.md` 가 그 관계를
 * 바꾸는 날 **이 문장이 가장 먼저 거짓이 된다.**
 *
 * ## ⛔ 수를 `view.pages.length` 로 세지 마라 (2026-10-03 정정)
 *
 * `pages` 는 **블록이 세우는 장**이고 `ASK` 에서는 **묻는 장**만이다. 앞 회차에서 `4번 사진` 은
 * 이미 `만들지 않기`, `6번 사진` 은 안 고른 8장 판이면 묻는 장은 1장인데 나가는 장은 6장이라
 * **파일은 2개 적다.** 옛 꼴은 `파일이 1개 적은` 이라고 말했다. 셀러가 받은 파일을 세어 맞춰
 * 보는 유일한 줄이 틀린 수를 말한 것이다.
 *
 * ⭐ **`total − made` 는 곧 `failedImages` 의 길이다.** 장이 `DONE` 아니면 `FAILED` 라서다.
 * 받치는 것은 상태마다 다르다. `ASK`·`SETTLED` 는 **판이 움직이지 않을 때만** 선다
 * (`exportDeclineStage` 의 「판이 움직인다」. `PENDING`·`PROCESSING` 장이 없다). ⚠️ `DONE` 은
 * 그 판정보다 **먼저** 갈리므로 움직임 판정이 받치지 않는다. 받치는 것은 서버 쪽 성질이다.
 * `computeJobStatus` 가 `EXPORTED` 를 고정하고, 장을 다시 돌리는 동안은 작업이 `PROCESSING` 이라
 * 화면이 서지 않는다. 그래도 `failedImages` 를 여기서 다시 세지 않고 `total`·`made` 로
 * 빼는 것은 **바로 앞 문장의 `${made}장` 과 같은 두 수에서 나와야** 셀러가 더해 맞출 수 있어서다. */
export function exportDeclineFileNote(view: ExportDeclineView): string {
  return `${view.made}장이 틀에 들어갔습니다. 파일이 ${view.total - view.made}개 적은 것은 그래서입니다.`;
}

/* ── 못 만든 장 블록 · 확인 시트 그룹 ────────────────────────────────────── */

/** 블록 한 행. **장마다 하나씩 선다.** ⛔ **접지 않는다** — 접힌 장은 **셀러가 보지 못한 채
 *  결판난다**(`7.6.14b` ⓒ 의 1번이 막는 자리). */
export interface ExportDeclineRow {
  imageId: string;
  /** 행 머리의 모노. **못 만든 장이 하나면 `null`** — 그때는 **블록 머리가 이미 그 장을
   *  가리킨다.** 둘 이상이면 그 자리가 어느 장인지 못 가리키므로 행으로 내려온다. */
  mono: string | null;
  amount: { label: string; value: string };
  /** `그 장 다시 만들기 ›` → `A-25b`. **링크는 문이지 동작이 아니다** — 내보내기 화면이
   *  만드는 일을 시작하지 않는다(`A-52b` 의 `상품명 적기 ›` 와 같은 규격).
   *
   * ⛔ **`4번 사진 다시 만들기 ›` 로 바꾸지 마라.** **행 머리의 모노가 어느 장인지 이미
   * 말한다.** 바꾸면 **같은 문 하나가 행마다 다른 이름**을 갖는다(`7.5.9` 규칙 2).
   * **내보낸 뒤에는 `null` 이다** — 갈 곳이 `13.1` O-70 3번에 매여 있어 **없는 문이고**,
   * **꺾쇠는 갈 곳이 있을 때만 붙인다**(`7.5.4`). */
  link: string | null;
}

export interface ExportDeclineBlock {
  head: string;
  /** 머리 우측 모노. **`n = 1` 일 때만** 선다. ⛔ **`4·6번 사진` 으로 잇지 않는다** —
   *  모노 한 칸은 **식별자 하나의 자리**다. */
  trailing: { mono: string } | null;
  rows: ExportDeclineRow[];
  note: string;
}

/** `A-52c` 의 못 만든 장 블록(`7.6.14b` ⓐ·ⓑ·ⓓ).
 *
 * ⛔ **블록을 장마다 세우지 않는다.** 머리의 수와 블록 설명은 **못 만든 장 전체를 두고 하는
 * 말**이라, 복제하면 **같은 말이 n 번 서고 `못 만든 1장` 이 세 번 뜬다.**
 * ⛔ **합계를 적지 않는다.** ⛔ **`외 n장` 으로 접지 않는다.** */
export function exportDeclineBlock(view: ExportDeclineView): ExportDeclineBlock {
  const pages = view.pages;
  // ⭐ **`SETTLED` 와 `DONE` 은 행의 모양이 같다.** 둘 다 **조건(`다시 만들지 않으면`)이
  //    이미 참**이고 **갈 곳이 없는 문을 그리지 않는다**(`7.6.14b` ⓔ). 블록 설명만 갈린다.
  const answered = view.stage !== "ASK";
  // **행이 하나일 때만 머리가 그 장을 가리킨다.** 길이를 두 번 보지 않고 그 장 자체를
  // 잡아 둔다 — 인덱스로 다시 집으면 "길이를 봤으니 있다"를 타입이 못 따라온다.
  const [only] = pages.length === 1 ? pages : [];
  return {
    head: `못 만든 ${pages.length}장`,
    trailing: only ? { mono: only.name } : null,
    rows: pages.map((page) => ({
      imageId: page.imageId,
      mono: only ? null : page.name,
      // ⭐ **`A-52c` 에는 고르는 자리가 없어 라벨이 갈릴 일이 없다** — `7.6.7d` ⓓ 의 라벨
      //    셋 중 **첫째 하나만** 이 화면에 선다. **내보낸 뒤에는 조건이 이미 참**이라
      //    조건절을 걷는다.
      amount: amountRow(roundEndAmountLabel(answered ? "DECLINE" : null), page.refundableAmount),
      // ⛔ **`SETTLED` 에도 링크를 세우지 않는다**(`7.6.14b` ⓔ). 셀러가 **이미 `만들지 않기`
      //    로 답한 장**이고, 되살리는 일이 `13.1` O-70 5번과 같은 자리인지가 안 갈렸다.
      link: answered ? null : "그 장 다시 만들기",
    })),
    note:
      view.stage === "DONE"
        ? exportedBlockNote(view)
        : view.stage === "SETTLED"
          ? settledBlockNote(view)
          : askBlockNote(pages),
  };
}

/** `A-52c` 블록 설명. ⛔ **행마다 되풀이하지 않는다** — **블록 끝에 한 번**이고 그 문장의
 *  `이 장` 은 **행마다 나눠 읽히는 말**이다(`A-24e` 가 같은 꼴을 같은 자리에 세운다). */
function askBlockNote(pages: readonly ExportDeclinePage[]): string {
  const base = "다시 만들면 이 장 금액이 이 장에 그대로 쓰이고, 더 내실 금액이 없습니다.";
  if (!hasUnknownAmount(pages)) return base;
  // ⭐ **둘째 문장의 동사가 `A-52d` 와 갈린다.** 이 화면에서 셀러가 하는 일은 **받는 것**
  //    이고 시트에서 하는 일은 **고르는 것**이다. ⛔ **한 꼴로 뭉치지 마라** — 이 화면에는
  //    고르는 자리가 없어 `고르실 수 있습니다` 가 **여기서 거짓**이다.
  return `${base} ${UNKNOWN_AMOUNT_NOTE} 금액이 적히지 않아도 받으실 수 있습니다.`;
}

/** **이미 결판났고 판은 아직 안 나간** 블록 설명(`7.6.14b` ⓔ).
 *
 * ⛔ **`받는 파일 ${파일 수}개는 그대로입니다.` 를 앞에 붙이지 않는다.** 그 문장이 서는 까닭은
 * 셀러가 **지금 그 선택을 하고 있어서**(`A-52d`) 또는 **방금 해서**(`내보낸 뒤`)인데, 여기서는
 * 그 선택이 **지난 회차의 일**이라 **`그대로` 가 가리킬 것이 없다.**
 *
 * ⚠️ **금액을 모르는 행이 섞여도 사유 문장을 붙이지 않는다. 정본에 그 칸이 없어서다.**
 * `7.6.14b` ⓓ 의 표는 `A-52c` · `A-52d` · `A-52c 내보낸 뒤` 셋만 적고, ⓔ 는 그 뒤에 섰다.
 * ⛔ **여기서 문장을 고르지 않는다.** 정본이 그 칸을 채우면 그때 붙인다. */
function settledBlockNote(view: ExportDeclineView): string {
  return `이 선택이 바꾸는 것은 그 ${view.pages.length}장의 일입니다.`;
}

/** **내보낸 뒤** 블록 설명. ⭐ **둘째 문장이 없다** — 그 화면에는 **받을 일도 고를 일도
 *  남아 있지 않다.** */
function exportedBlockNote(view: ExportDeclineView): string {
  const base =
    // ⛔ `받은 파일` 로 되돌리지 마라. 셀러가 파일을 손에 들었다고 단언하는 말이고,
    //    판이 나간 것과 셀러가 받은 것은 다르다. 앱은 2026-10-05 부터 받지만(`7.13` D-91)
    //    폴더 고르기를 닫고 떠날 수 있다. 이 자리가 하는 일은
    //    **선택이 파일 수를 안 바꿔다**를 말하는 것이지 수령을 알리는 것이 아니다.
    //    낱말은 `7.6.14` 배너가 새로 고른 `파일이 만들어지고` 에서 그대로 따온다.
    `만들어진 파일 ${view.files}개는 그대로입니다.` +
    ` 이 선택이 바꾼 것은 그 ${view.pages.length}장의 일입니다.`;
  if (!hasUnknownAmount(view.pages)) return base;
  return `${base} ${UNKNOWN_AMOUNT_NOTE}`;
}

/* ── 발밑 액션바 ─────────────────────────────────────────────────────────── */

export type ExportActionKey = "share" | "save";

/** 발밑 버튼 하나. **`key` 가 이 버튼이 무슨 일을 하는가이고, 화면이 그것으로 동작을 건다**
 *  (앱 `apps/mobile/src/lib/result.ts` 의 `ResultAction` 과 같은 규율). **라벨을 보고 동작을 고르지 않는다.** */
export interface ExportAction {
  key: ExportActionKey;
  label: string;
  variant: "primary" | "ghost";
  disabled: boolean;
  /** 꺼진 사유. **앱에는 툴팁이 없어 항상 보이는 글로 함께 쓴다.** */
  disabledReason?: string;
}

/** `A-52c` 하단 액션바.
 *
 * ⭐ **`끄지 않는다` 가 걸린 것은 `받기`(primary)다**(`7.6.14b` 의 정정). **받기를 끄면 이
 * 갈래의 화면이 통째로 못 선다** — `A-52d` 는 **누른 사람에게만 가는데** 누를 것이
 * 없어지고, 그러면 **`만들지 않기` 를 고르는 자리가 앱에서 사라진다**(`13.1` O-70 의 두
 * 경로가 다시 동시에 빈다).
 *
 * **`공유`(ghost)도 켜져 있다**(2026-10-05. `7.13` D-91). 앱 `A-52` 가 묶음을 받아 OS 공유
 * 시트로 넘긴다(`apps/mobile/src/app/detail/export.tsx`). 그전에는 전달 경로가 없어 꺼 두고
 * 사유 `받기만 됩니다. 공유는 아직 없습니다.` 를 달았다. ⭐ **켜진 공유도 `A-52d` 를 지난다**
 * (`exportActionOpensConfirm`). 공유만 시트를 건너뛰면 그 길이 몰래 `만들지 않기` 가 된다.
 * 웹 `W-40` 은 이 함수를 쓰지 않는다.
 *
 * ⛔ **발밑에 `만들지 않기` 를 두지 않는다.** **화면을 보고 있는 것만으로는 아직 고른 것이
 * 아니다** — 선택지를 화면에 박으면 **구경하러 들어온 셀러에게까지 결판을 요구한다.**
 * ⭐ **물음은 누른 사람에게만 간다.** */
export function exportActions(files: number): ExportAction[] {
  return [
    {
      key: "share",
      label: "공유",
      variant: "ghost",
      // 앱이 묶음을 공유 시트로 넘긴다(`7.13` D-91). 꺼 두던 두 줄(끔 · 사유)을 걷었다.
      disabled: false,
    },
    // 내비 우측과 **같은 숫자**를 쓴다. 두 자리가 갈리면 셀러가 몇 개를 받는지 세게 된다.
    // ⛔ **이 버튼은 끄지 않는다**(위 머리말).
    { key: "save", label: `${files}개 파일 받기`, variant: "primary", disabled: false },
  ];
}

/** ⭐⭐ **`공유` 도 `받기` 도 `A-52d` 를 연다.** `7.6.14b`: *둘 다 판을 내보내는 일이라
 *  결말이 같다.* ⛔ **공유만 시트를 건너뛰면 그 길이 몰래 `만들지 않기` 가 된다** — 셀러는
 *  파일을 넘겼을 뿐인데 그 장이 결판나고 돈이 움직인다.
 *
 * ⛔ **`key` 로 갈래를 만들지 마라.** 이 함수가 `key` 를 받는 것은 **두 키가 같은 답을
 * 받는다는 사실을 시험이 키마다 단정할 수 있게** 하기 위해서지 갈래를 두기 위해서가 아니다.
 *
 * **`asking` 이 거짓이면 열지 않는다.** ⛔ **두 번째 `받기` 부터는 시트를 띄우지 않는다** —
 * **이미 결판난 장을 또 묻는 일**이고, 그 시트는 **아직 결판이 안 난 장**에만 선다. */
export function exportActionOpensConfirm(key: ExportActionKey, asking: boolean): boolean {
  // `key` 를 읽되 **가르지 않는다.** 둘 다 같은 답이다.
  void key;
  return asking;
}

/** 이 화면이 지금 묻는 중인가. **`A-52d` 를 여는 유일한 조건**이다.
 *
 * ⭐ **`SETTLED` 는 묻지 않는다**(`7.6.14b` ⓔ). 결판이 이미 났으므로 **받기는 시트 없이 바로
 * 나간다.** ⛔ **`stage !== "DONE"` 으로 바꾸지 마라**. 셀러가 이미 답한 장을 판 끝에서
 * 다시 묻는다. */
export function exportIsAsking(view: ExportDeclineView | null): boolean {
  return view?.stage === "ASK";
}

/* ── `✓` 배너. 앱과 웹이 갈리는 한 문장 ─────────────────────────────────── */

/** `✓` 배너 앞 두 문장. **앱과 웹이 같다**(`7.6.14` · `7.7.5a`). */
const EXPORT_END_LEAD = "여기가 끝입니다. 아래 받기를 누르면 파일이 만들어지고 이 작업이 끝납니다.";

/** `✓` 배너 전문(앱 `7.6.14` · 웹 `7.7.5a`. PRD `7.13` D-93 ⓑ).
 *
 * ⭐⭐ **갈리는 것은 셋째 문장 하나이고, 가르는 것은 「이 클라이언트가 파일을 손에 쥐어 줄 수
 * 있는가」 하나다**(`7.7.5a`). 그래서 인자가 클라이언트 이름이 아니라 그 사실이다. **앱이 묶음을
 * 받게 되는 날(`7.13` D-91) 앱이 `true` 를 넘기면 문장이 맞는다.** ⛔ **`"web"`·`"app"` 으로
 * 가르지 마라.** 그러면 D-91 이 닫히는 날 이 함수의 갈래를 고쳐야 하고 한쪽만 고쳐진다.
 *
 * - `true` (웹 `W-40c`·`W-40e`, 앱 `A-52`·`A-52c`. 앱은 2026-10-05 부터): **받은 파일을 어디에 붙여 넣는지.** 목업 `W-40c`·`W-40e` 의
 *   `✓` 배너 그대로다. ⛔ 앱 문장을 웹에 쓰면 거짓이다. **여기가 그 웹이다.**
 * - `false` (파일을 못 넘기는 클라이언트. 지금 부르는 자리는 없다): **이 클라이언트에서 아직 못 받으니 웹에서 받으라**(`7.6.14`).
 *   ⛔ **제품이 파일을 못 준다고 적지 않는다.** 못 하는 것은 **이 클라이언트**다.
 *   ⛔ **셀러가 파일을 이미 손에 들었다고 적지 않는다**(`scripts/screen-wording.test.mjs` 의
 *   금지어가 이 갈래의 값을 직접 본다).
 *
 * 앱 화면(`apps/mobile/src/app/detail/export.tsx`)은 `HANDS_OVER_FILES`(= `true`)로 이 함수를
 * 부른다. 앱이 묶음을 받아 폴더 저장 · 공유 시트로 넘기기 때문이다(`7.13` D-91).
 * ⛔ **`!` 로 바꾸지 않는다.** 아이콘이 말하는 것은 **1단계 제품의 범위가 여기까지**라는 사실이고
 * 그것은 양쪽에 참이다. `!` 자리는 맨 위 제한 배너가 쥐고 있다. */
export function exportEndBanner(handsOverFiles: boolean): string {
  if (handsOverFiles) {
    return `${EXPORT_END_LEAD} 받은 파일을 스마트스토어센터 상품등록 → 상세설명에 순서대로 붙여 넣으면 상품이 올라갑니다.`;
  }
  return `${EXPORT_END_LEAD} 다만 그 파일을 앱에서 아직 받을 수 없어, 지금은 웹에서 받아 스마트스토어센터 상품등록 → 상세설명에 순서대로 붙여 넣습니다.`;
}

/* ── `A-52d` 확인 시트 ───────────────────────────────────────────────────── */

/** 시트 제목. **제목이 일을 통째로 말하고 버튼은 그 일의 동사다**(`A-42` 의 문법).
 *  ⭐ **무엇이 결판나는지를 버튼 바로 위에서 센다**(`7.6.14b` ⓒ 의 3번). */
export function exportConfirmTitle(view: ExportDeclineView): string {
  return `못 만든 ${view.pages.length}장은 만들지 않기로 하고 내보낼까요?`;
}

/** 시트 본문 세 문장.
 *
 * - ⚠️ **이 본문은 사용자 결정 D2(2026-10-04) 위에서만 참이다.** D2: 웹 · 앱에서 「만들지 않기」를
 *   고르면 환불정책 제4조의 「알려 준 것」으로 본다(`public-copy.ts` `REFUND_RETURN_TIMING` 둘째
 *   문장 · 법무 `L-F56` 선행조건 7). 그래서 「따로 알려 주실 것이 없습니다」가 참이다. D2 를
 *   뒤집으면(제4조 둘째 문장을 걷으면) 이 문장은 거짓이 되니 함께 고친다.
 *   옛 끝 문장 「신청할 것이 없습니다」(`A-31b` 와 같은 말)는 2026-10-04 이 문장으로 바꿨다(PRD `7.0`
 *   「로그인 전 화면의 확정 문구」). 앱 `A-31b` 의 그 문장은 앱 회차에 따로 본다.
 * - ⛔ **`이 장 금액이` 로 되돌리지 마라.** **주어가 복수인 문장에 금액이 하나 딸리면**
 *   셀러는 **한 숫자가 돌아온다**고 읽고 **그 숫자를 화면에서 찾는다. 찾을 수 없다** —
 *   합계는 이 판 어디에도 서지 않는다(`7.6.7d` ⓐ).
 * - ⛔ **`장마다 그 금액이 돌아갑니다` 에 조건절을 달지 마라.** 값을 몰라도 참이다.
 *   **얼마인지를 모르는 것**이지 **돌아가지 않는 것**이 아니다. 「그때」는 2026-10-04 걷었다.
 *   돌아가는 때는 환불정책 제4조가 말하고, 이 시트가 때를 따로 약속하지 않는다. */
export function exportConfirmBody(): string {
  return (
    "지금 받으면 이 작업이 끝납니다." +
    " 못 만든 장은 만들지 않은 것으로 확정되고, 장마다 그 금액이 돌아갑니다." +
    " 따로 알려 주실 것이 없습니다."
  );
}

/** 시트 안의 그룹. ⚠️ **「수만 적은 요약」으로 줄이지 마라** — 줄이면 **누르기 직전 화면에
 *  그 장이 전부 있다**는 전제가 무너지고, `A-24e` 가 막으려던 일(**보지 않은 장을 한 번에
 *  잃는 일**)이 이 시트에서 일어난다(`7.6.14b` ⓒ 의 2번). */
export function exportConfirmBlock(view: ExportDeclineView): ExportDeclineBlock {
  const pages = view.pages;
  const [only] = pages.length === 1 ? pages : [];
  return {
    head: `못 만든 ${pages.length}장`,
    trailing: only ? { mono: only.name } : null,
    rows: pages.map((page) => ({
      imageId: page.imageId,
      mono: only ? null : page.name,
      // ⭐ **조건절만 걷었다.** 뒤 화면은 `다시 만들지 않으면 돌아갈 금액` 이라 적는데
      //    **여기서는 셀러가 바로 그것을 고르는 중**이라 조건이 이미 참이다.
      amount: amountRow(roundEndAmountLabel("DECLINE"), page.refundableAmount),
      // ⛔ **시트에는 링크를 두지 않는다.** 살릴 장이 있으면 **닫고** 뒤 화면의 그 링크로
      //    간다 — `pickhint` 가 그 순서를 적고 있다.
      link: null,
    })),
    note: confirmBlockNote(view),
  };
}

function confirmBlockNote(view: ExportDeclineView): string {
  const base =
    `받는 파일 ${view.files}개는 그대로입니다.` +
    ` 이 선택이 바꾸는 것은 그 ${view.pages.length}장의 일입니다.`;
  if (!hasUnknownAmount(view.pages)) return base;
  return `${base} ${UNKNOWN_AMOUNT_NOTE} 금액이 적히지 않아도 고르실 수 있습니다.`;
}

/** 버튼 아래 사유(`pickhint`). **장별로 가르고 싶은 셀러의 길이 여기 적혀 있다** —
 *  닫고 뒤 화면의 `그 장 다시 만들기 ›` 로 간다(`7.6.7d` ⓒ: `다시 만들기` 를 고른 장은
 *  **더 이상 못 만든 장이 아니다**). */
export function exportConfirmPickHint(): string {
  return (
    "다시 만들려면 닫고 위 그 장 다시 만들기를 눌러 주세요." +
    " 닫으면 아무것도 나가지 않고 그 장도 그대로 있습니다."
  );
}

/** 시트 발밑 버튼 둘.
 *
 * - ⛔ **`만들지 않고 받기` 로 늘리지 않는다.** 같은 선택이 **두 이름**을 갖고 `A-24e` 의
 *   낱말이 **여기서만 달라진다.** **받는 일은 제목이 이미 말했다.**
 * - ⛔ **`danger` 를 쓰지 않는다.** 그 색은 **가진 것이 사라지는 일**의 색인데 여기서 셀러가
 *   잃는 것이 없다 — **파일은 그대로 나가고 돈은 돌아온다.**
 * - **`그만두기` 가 아니라 `닫기` 다.** `그만두기` 는 `A-25b` 에서 **다시 만들기를
 *   그만둔다**는 뜻으로 이미 서 있어, 여기 쓰면 **작업을 그만둔다**로 읽힌다(`7.5.9`). */
export interface ExportConfirmActions {
  primary: { label: string; disabled: boolean };
  secondary: { label: string };
}

export function exportConfirmActions(): ExportConfirmActions {
  return {
    // ⭐ **기본 선택은 없지만 버튼은 눌린다.** 시트가 뜨는 것과 **답이 미리 찍혀 있는 것은
    //    다르다** — **묻되 답하지 않는다.** 고르는 일은 이 버튼을 누르는 것 하나다.
    primary: { label: "만들지 않기", disabled: false },
    secondary: { label: "닫기" },
  };
}

/** 시트에서 셀러가 누른 것. **닫는 길 전부가 `CLOSE` 다** — 하단 `닫기` · 시트 밖 누르기 ·
 *  시스템 뒤로. */
export type ExportConfirmPress = "CLOSE" | "DECLINE";

/** 그 누름이 실제로 하는 일.
 *
 * - `NOTHING` — **닫으면 아무 일도 안 일어난다.** 판도 안 나가고 그 장도 그대로 있다.
 * - `DECLINE_AND_EXPORT` — 그 장을 **만들지 않기로 적고** 판을 내보낸다. */
export type ExportConfirmOutcome = "NOTHING" | "DECLINE_AND_EXPORT";

/** ⭐⭐ **고르지 않으면 판이 안 나간다.** `A-24e` 와 **정반대이고 그것이 설계다**
 * (`7.6.14b`).
 *
 * `A-24e` 는 **고르지 않아도 주 버튼이 눌린다.** 근거는 **회차가 끝날 때마다 다시 묻는다**
 * 였다. **여기에는 다음 회차가 없어 같은 근거가 반대 결론을 낸다.**
 *
 * ⛔ **`CLOSE` 를 내보내기로 잇지 마라.** 그러면 **받기 버튼이 몰래 `만들지 않기` 가 되고**,
 * 그것은 **앱이 셀러 대신 고르는 일**이라 `A-24e` 가 이미 금지한 것이다. ⭐ **미룸이
 * 사라지는 것이 아니라 「내보내면서 미루는 길」 하나가 없는 것이다.** */
export function exportConfirmOutcome(press: ExportConfirmPress): ExportConfirmOutcome {
  if (press === "CLOSE") return "NOTHING";
  return "DECLINE_AND_EXPORT";
}

/* ── 금액 ────────────────────────────────────────────────────────────────── */

/** 금액을 모르는 장이 섞였을 때 설명 끝에 붙는 첫 문장.
 *
 * ⚠️ **구현 상태를 조건절로 지고 있다**(`결제가 아직 붙지 않아`). `7.5.9` 가 명시로 면제한
 * 꼴이고, **결제가 붙는 날 거짓이 되므로 그날 전수로 다시 쓴다.** */
const UNKNOWN_AMOUNT_NOTE = "결제가 아직 붙지 않아 금액이 적히지 않은 장이 있습니다.";

/** ⚠️ **조건은 「이 화면에 선 행」이다.** ⛔ **`refundableTotal(job).unknownCount` 를 그대로
 *  쓰지 마라** — **이미 결판난 장까지 세어**, 서 있는 행이 전부 숫자인 화면에도 그 문장을
 *  세운다. */
function hasUnknownAmount(pages: readonly ExportDeclinePage[]): boolean {
  return pages.some((page) => page.refundableAmount === null);
}

/** 금액 줄 하나. **숫자는 안 바뀌고 라벨만 바뀐다**(`7.5.9` 판정 10).
 *
 * ⛔ **`₩0` 을 적지 않는다.** `0` 은 **돌아갈 돈이 없다**는 거짓 문장이다.
 * ⛔ **단가를 곱해 지어내지 않는다** — 단가는 **장마다 다를 수 있다**(다시 만들기가 낀 작업).
 * ⭐ **`없음` 이 아니라 `알 수 없음` 이다.** `없음` 은 **없다는 것을 안다**는 말인데 여기는
 * **모른다.** `A-24e` 가 같은 자리에서 같은 말을 쓴다(`7.5.9` 판정 12 가 이 자리에 한해
 * 판정 11 의 「선례로 쓰지 마라」를 풀었다).
 * ⛔ **금액을 몰라도 줄은 선다** — 빼면 **줄의 유무가 `₩0` 을 말한다.** 옆 행에는 `₩500`
 * 이 서 있다. */
function amountRow(label: string, refundableAmount: number | null): { label: string; value: string } {
  if (refundableAmount === null) return { label, value: "알 수 없음" };
  return { label, value: formatKrw(refundableAmount) };
}
